/**
 * Dashboard Page — Operator command centre
 *
 * Design:
 * - Hero stats with visual weight and colour coding
 * - Active jobs prominent when running, collapsed when idle
 * - Attention items always visible if non-zero
 * - Timeline grouped by date with clean visual hierarchy
 * - Single API call (GET /dashboard), 10s auto-refresh
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';
import {
  Upload, FileText, Newspaper, AlertTriangle, CheckCircle2,
  Loader2, Clock, Send, XCircle, RefreshCw, Wifi, WifiOff,
  ArrowRight, Zap, Globe, BookOpen, Eye, GitMerge,
  ChevronDown, ChevronUp, ExternalLink, Flame, Link2,
} from 'lucide-react';
import { dashboardQueryOptions, getDashboard, archiveFailedJobs, retryFailedJobs, archiveFailedAnnouncements } from '../lib/api/dashboard.js';
import type { DashboardJob, DashboardAttentionItem, ArticlesBySiteRow, ArticlesBySectorRow, CoverageLaneRow, MandatedCoveragePanel, ActionQueueBlock, HeartbeatBlock, BufferBudget, FirecrawlBudget, LinkedInApiBudget, CapacityQueueBlock, MastheadCapSnapshot, SocialCapacity } from '../lib/api/dashboard.js';
import {
  listContactMergeCandidates,
  listCompanyMergeCandidates,
} from '../lib/api/merge-candidates.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { CommissionsCard } from '../components/CommissionsCard.js';
import { SocialPostingBars } from '../components/dashboard/SocialPostingBars.js';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const JOB_CONFIG: Record<string, { label: string; icon: typeof FileText; bg: string; text: string }> = {
  process:              { label: 'Processing',      icon: RefreshCw, bg: 'bg-info-100',    text: 'text-info-700' },
  generate:             { label: 'Generating',      icon: Zap,       bg: 'bg-warning-100', text: 'text-warning-700' },
  publish:              { label: 'Publishing',      icon: Send,      bg: 'bg-primary-100', text: 'text-primary-700' },
  unpublish:            { label: 'Unpublishing',    icon: XCircle,   bg: 'bg-grey-100',    text: 'text-grey-700' },
  image_enrichment:     { label: 'Images',          icon: Eye,       bg: 'bg-info-100',    text: 'text-info-700' },
  ingest:               { label: 'Ingesting',       icon: RefreshCw, bg: 'bg-success-100', text: 'text-success-700' },
  research:             { label: 'Researching',     icon: Zap,       bg: 'bg-purple-100',  text: 'text-purple-700' },
  signal:               { label: 'Signal Analysis', icon: Zap,       bg: 'bg-amber-100',   text: 'text-amber-700' },
  synthesis_generate:   { label: 'Synthesis',       icon: Zap,       bg: 'bg-purple-100',  text: 'text-purple-700' },
  synthesis_detect:     { label: 'Thread Detection',icon: Eye,       bg: 'bg-purple-100',  text: 'text-purple-700' },
  migrate:              { label: 'Migration',       icon: RefreshCw, bg: 'bg-grey-100',    text: 'text-grey-700' },
};

const ACTIVE_STATUSES = new Set([
  'running', 'extracting', 'enriching', 'generating', 'queued', 'uploaded', 'pending', 'retrying',
]);

// Map each job_type to the service that does the bulk of the work, so the
// dashboard can colour cards by service rather than job_type.
//
// - publishing: work whose bulk happens in assayer-publishing-service.
//   `publish`, `unpublish` and `promote_article` are claimed by that service's
//   own worker. `build_deploy` is the exception worth knowing about: the
//   assayer-backend worker CLAIMS it, then holds an open HTTP request while
//   publishing does the clone / eleventy / pagefind / wrangler work — minutes,
//   against seconds of backend time. Attributed here for the same reason as
//   cynapse below: when builds back up, the service to scale is publishing.
// - cynapse: claimed by the assayer-backend worker but spend most of their
//   time inside cynapse (PDF rendering / image extraction). Useful as a
//   visual cue for "if these slow, scale cynapse" rather than backend.
// - backend (default): everything else runs end-to-end in assayer-backend.
// 'signals' is a pseudo-service: it is NOT rendered as its own column — signal
// jobs are high-volume and publish to a dedicated branch on a relaxed cadence,
// so they'd swamp the publishing column. Instead we surface their depth as a
// separate count in the publishing header.
type JobService = 'backend' | 'publishing' | 'cynapse' | 'signals';

const SIGNAL_JOB_TYPES = new Set(['publish_signal', 'promote_signals_batch']);
const PUBLISHING_JOB_TYPES = new Set(['publish', 'unpublish', 'promote_article', 'build_deploy']);
const CYNAPSE_JOB_TYPES = new Set(['image_enrichment', 'source_announcement']);

function jobService(jobType: string): JobService {
  if (SIGNAL_JOB_TYPES.has(jobType)) return 'signals';
  if (PUBLISHING_JOB_TYPES.has(jobType)) return 'publishing';
  if (CYNAPSE_JOB_TYPES.has(jobType)) return 'cynapse';
  return 'backend';
}

// Colour palette per service for active-job cards. Border + bg + icon shades.
const SERVICE_COLORS: Record<JobService, { border: string; bg: string; iconBg: string; icon: string; text: string; label: string }> = {
  backend:    { border: 'border-primary-200', bg: 'bg-primary-50', iconBg: 'bg-primary-200', icon: 'text-primary-700', text: 'text-primary-600', label: 'Assayer AI' },
  publishing: { border: 'border-success-200', bg: 'bg-success-50', iconBg: 'bg-success-200', icon: 'text-success-700', text: 'text-success-600', label: 'publishing' },
  // 'signals' is never rendered as its own column (see JobService comment);
  // this entry only satisfies the Record type. Its depth shows in the
  // publishing header.
  signals:    { border: 'border-grey-200', bg: 'bg-grey-50', iconBg: 'bg-grey-200', icon: 'text-grey-700', text: 'text-grey-600', label: 'signals' },
  // "Cynapse Enrichment" — these jobs are claimed and run by assayer-backend's
  // worker, but the bulk of wall-clock time is spent calling cynapse for
  // PDF rendering / image extraction. The label reflects bottleneck
  // attribution rather than literal job ownership.
  cynapse:    { border: 'border-orange-200',  bg: 'bg-orange-50',  iconBg: 'bg-orange-200',  icon: 'text-orange-700',  text: 'text-orange-600',  label: 'Cynapse Enrichment' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A scheduled time with its date — "Fri 9 Oct, 22:00" — so a job due on
 *  another day never reads as due today (operator 2026-10-06). */
export function formatDue(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Running first, then queued work in the order it will run (due time). */
export function sortActiveJobs<T extends { status: string; next_run_at?: string | null; created_at: string }>(jobs: T[]): T[] {
  const at = (j: T) => new Date(j.next_run_at || j.created_at).getTime();
  return [...jobs].sort((a, b) =>
    Number(b.status === 'running') - Number(a.status === 'running') || at(a) - at(b));
}

function timeAgo(dateStr: string | null | undefined): string {
  // Guard nullish/empty input — `new Date(null)` returns the epoch and would
  // render "over 56 years ago" on cards where created_at hasn't been set yet
  // (e.g. announcement rows whose processing_started_at is null).
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  try { return formatDistanceToNow(d, { addSuffix: true }); }
  catch { return ''; }
}

function jobTitle(job: DashboardJob): string {
  // Parse payload if it's a JSON string (asyncpg may return JSONB as string in some cases)
  let payload = job.payload;
  if (typeof payload === 'string') {
    try { payload = JSON.parse(payload); } catch { payload = {}; }
  }
  if (!payload || typeof payload !== 'object') payload = {};

  // Article-related jobs: show the article or announcement title
  if (job.article_title) return job.article_title;
  if (job.announcement_title) return job.announcement_title;
  if (payload.announcement_title) return payload.announcement_title;

  // Ingest jobs: "ASX Direct — BHP, FMG, CBA, CSL"
  if (job.job_type === 'ingest') {
    const source = payload.source_name;
    const entities = payload.entity_codes as string[] | undefined;
    if (source && entities?.length) {
      const preview = entities.length <= 4 ? entities.join(', ') : `${entities.slice(0, 3).join(', ')} +${entities.length - 3} more`;
      return `${source} — ${preview}`;
    }
    if (source) return source;
  }

  // Publish jobs: "orecast / test"
  if (job.job_type === 'publish' || job.job_type === 'unpublish') {
    const target = payload.target;
    const dest = payload.destination;
    if (target) return `${target}${dest ? ` / ${dest}` : ''}`;
  }

  // Research jobs
  if (job.job_type === 'research') {
    return payload.entity_name || payload.entity_code || 'Entity research';
  }

  // Signal processing
  if (job.job_type === 'signal') return 'Trend signal aggregation';

  // Synthesis
  if (job.job_type === 'synthesis_generate') return payload.thread_name || 'Synthesis';
  if (job.job_type === 'synthesis_detect') return 'Pattern detection';

  // Fallback: show company if available
  if (job.company_name) return job.company_name;
  if (job.company_code) return job.company_code;

  return job.job_type || 'Job';
}

function jobResultSummary(job: DashboardJob): string | null {
  if (job.status !== 'completed') return null;

  let payload = job.payload;
  if (typeof payload === 'string') {
    try { payload = JSON.parse(payload); } catch { payload = {}; }
  }

  // Ingest result from processor stats
  const result = job.result;
  if (result) {
    const r = typeof result === 'string' ? JSON.parse(result) : result;
    if (r.fetched !== undefined) {
      if (r.created > 0) return `${r.created} ingested, ${r.duplicates || 0} duplicates`;
      if (r.fetched === 0) return 'Nothing found';
      return `${r.fetched} checked, ${r.matched || 0} matched`;
    }
  }

  if (job.error_message) return job.error_message;
  return null;
}

function jobLink(job: DashboardJob): string {
  if (job.payload?.article_id) return `/articles/${job.payload.article_id}`;
  if (job.announcement_id) return `/announcements/${job.announcement_id}`;
  if (job.job_type === 'ingest') return '/pipeline?tab=monitors';
  if (job.job_type === 'research') return '/pipeline?tab=monitors';
  if (job.job_type === 'signal') return '/intelligence';
  if (job.job_type === 'publish' || job.job_type === 'unpublish') return '/publishing';
  return '#';
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    extracting: 'Extracting',
    enriching: 'Enriching',
    generating: 'Generating',
    queued: 'Queued',
    uploaded: 'Uploaded',
    running: 'Running',
    pending: 'Pending',
    retrying: 'Retrying',
  };
  return labels[status] || status;
}

function groupByDate(jobs: DashboardJob[]): Array<{ label: string; jobs: DashboardJob[] }> {
  const groups: Map<string, DashboardJob[]> = new Map();
  for (const job of jobs) {
    const d = new Date(job.updated_at);
    let label: string;
    if (isToday(d)) label = 'Today';
    else if (isYesterday(d)) label = 'Yesterday';
    else label = format(d, 'EEE, d MMM');
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(job);
  }
  return Array.from(groups.entries()).map(([label, jobs]) => ({ label, jobs }));
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const queryClient = useQueryClient();
  // The shared autoInvalidate path only invalidates the resource type from
  // the channel name (e.g. ['announcements']), not ['dashboard']. Subscribe
  // explicitly to the events that change dashboard state and invalidate the
  // dashboard query so cards update via WS rather than only the 10s poll.
  const invalidateDashboard = () => queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  const { connected } = useWebSocket({
    channels: ['assayerai:announcements'],
    autoInvalidate: true,
    onEvent: {
      status_change:       invalidateDashboard,
      publish_started:     invalidateDashboard,
      publish_completed:   invalidateDashboard,
      publish_failed:      invalidateDashboard,
      unpublish_started:   invalidateDashboard,
      unpublish_completed: invalidateDashboard,
      unpublish_failed:    invalidateDashboard,
      promote_started:     invalidateDashboard,
      promote_completed:   invalidateDashboard,
      promote_failed:      invalidateDashboard,
      article_created:     invalidateDashboard,
    },
    debug: false,
  });

  const { data, isLoading } = useQuery(dashboardQueryOptions(10_000));

  const c = data?.counts;
  const activeJobs = data?.active_jobs || [];
  const timeline = data?.recent_timeline || [];
  const attention = data?.needs_attention || [];
  const timelineGroups = groupByDate(timeline);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-grey-900">Dashboard</h1>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            connected ? 'bg-success-100 text-success-700' : 'bg-grey-100 text-grey-400'
          }`}>
            {connected ? <><Wifi className="h-3 w-3" /> Live</> : <><WifiOff className="h-3 w-3" /> Offline</>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-grey-400 hidden sm:block">
            {format(new Date(), 'EEEE, d MMMM yyyy')}
          </span>
          <Link to="/upload" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 shadow-sm transition-colors">
            <Upload className="h-4 w-4" /> Upload
          </Link>
        </div>
      </div>

      {/* ── Stats Grid ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          icon={FileText} label="Announcements today" value={c?.announcements_today}
          sub={c?.total_announcements != null ? `${c.total_announcements} total` : undefined}
          accent="info"
          link="/announcements"
        />
        <StatCard
          icon={BookOpen} label="Articles" value={c?.total_articles}
          sub={c ? `${c.draft_articles} draft \u00b7 ${c.approved_articles} approved` : undefined}
          link="/articles"
        />
        <StatCard
          icon={Globe} label="Published" value={c?.published_articles}
          accent="success" link="/articles"
        />
        <BufferBudgetCard budget={data?.buffer_budget} />
        <FirecrawlCreditsCard budget={data?.firecrawl_budget} />
        {data?.linkedin_api?.connected && <LinkedInApiCard budget={data.linkedin_api} />}
      </div>

      {/* ── Per-site pacing + sector mix (editorial cut) ───────── */}
      <SitePerformanceStrip
        bySite={data?.articles_by_site || []}
        bySector={data?.articles_by_sector || []}
        capacity={data?.capacity_queue}
      />

      {/* ── Today's throughput (demoted from headline cards) ── */}
      <div className="text-xs text-grey-500 flex flex-wrap gap-x-4 gap-y-1">
        <span>{c?.articles_today ?? 0} articles today</span>
        <span>{c?.signals_published_today ?? 0} signals today</span>
        <span>
          {c?.backdated_articles_today ?? 0} backdated articles today
          {c?.backdated_announcements_today != null
            ? ` (${c.backdated_announcements_today} source jobs)`
            : ''}
        </span>
      </div>

      {/* ── Quick Nav ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <NavPill to="/announcements" icon={FileText} label="Announcements" />
        <NavPill to="/articles" icon={Newspaper} label="Articles" />
        <NavPill to="/sites" icon={Globe} label="Sites" />
        {/* Failed pill retired 07-23 — failures live in the Action Queue with ages + actions */}
      </div>

      {/* ── Active Jobs (split per service) ─────────────────── */}
      {(activeJobs.length > 0 || (data?.queue_depth?.total ?? 0) > 0) && (() => {
        const depth = data?.queue_depth;

        // Bucket the (capped) active_jobs by service so each column renders
        // its own running/pending cards. Total queue depth per service comes
        // from queue_depth.by_job_type (uncapped) — the cards are a sample,
        // the depth chip is the truth.
        const cardsByService: Record<JobService, DashboardJob[]> = { backend: [], cynapse: [], publishing: [], signals: [] };
        const runningByService: Record<JobService, number> = { backend: 0, cynapse: 0, publishing: 0, signals: 0 };
        for (const j of activeJobs) {
          const svc = jobService(j.job_type);
          cardsByService[svc].push(j);
          if (j.status === 'running') runningByService[svc] += 1;
        }
        const depthByService: Record<JobService, number> = { backend: 0, cynapse: 0, publishing: 0, signals: 0 };
        if (depth?.by_job_type) {
          for (const [jt, n] of Object.entries(depth.by_job_type)) {
            depthByService[jobService(jt)] += n;
          }
        }
        // Waiting (delayed) split — a job pending on a future next_run_at is on
        // a timer, not stuck. Bucket per service so each column shows how much
        // of its "queued" is really just waiting on the clock.
        const waitingByService: Record<JobService, number> = { backend: 0, cynapse: 0, publishing: 0, signals: 0 };
        // Scheduled-ahead summary per column, one line per job type, soonest
        // first. Signals have no column of their own; they sit with publishing.
        const scheduledByService: Record<JobService, NonNullable<typeof depth>['waiting_by_job_type']> =
          { backend: [], cynapse: [], publishing: [], signals: [] };
        for (const w of depth?.waiting_by_job_type ?? []) {
          waitingByService[jobService(w.job_type)] += w.count;
          const col = jobService(w.job_type) === 'signals' ? 'publishing' : jobService(w.job_type);
          scheduledByService[col].push(w);
        }
        for (const list of Object.values(scheduledByService)) {
          list.sort((a, b) => (a.next_due ?? '').localeCompare(b.next_due ?? ''));
        }
        const totalWaiting = depth?.waiting ?? 0;
        const totalDepth = depth?.total ?? activeJobs.length;

        // Cap each column to keep the page from becoming a wall when one
        // service dominates (the publish remediation backlog is a good
        // example — 130+ in queue, only first 8 shown here).
        const PER_COLUMN_CAP = 8;
        const MOBILE_CARD_CAP = 3;

        const services: JobService[] = ['backend', 'cynapse', 'publishing'];
        return (
          <section>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
              <h2 className="text-sm font-semibold text-grey-900 uppercase tracking-wider">Active Jobs</h2>
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-primary-100 text-primary-700">{totalDepth}</span>
              {totalWaiting > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700"
                  title="Pending jobs on a timer (delayed dispatch / retry backoff) — waiting, not stuck"
                >
                  {totalWaiting} waiting
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {services.map(svc => {
                const palette = SERVICE_COLORS[svc];
                const cards = sortActiveJobs(cardsByService[svc]);
                const queueLen = depthByService[svc];
                const running = runningByService[svc];
                const visible = cards.slice(0, PER_COLUMN_CAP);
                const overflow = queueLen - visible.length;
                return (
                  <div key={svc} className={`rounded-xl border ${palette.border} ${palette.bg} p-3 min-w-0`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`h-2 w-2 rounded-full ${palette.iconBg}`} />
                      <h3 className={`text-xs font-bold uppercase tracking-wider ${palette.icon}`}>{palette.label}</h3>
                      <span className="ml-auto text-xs text-grey-600">
                        <span className="font-semibold">{running}</span> running · <span className="font-semibold">{queueLen}</span> queued
                        {waitingByService[svc] > 0 && (
                          <> · <span className="font-semibold text-amber-600">{waitingByService[svc]}</span> waiting</>
                        )}
                        {svc === 'publishing' && (
                          <> · <span className="font-semibold">{depthByService.signals}</span> signals</>
                        )}
                      </span>
                    </div>
                    {scheduledByService[svc].length > 0 && (
                      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5" aria-label={`${palette.label} scheduled jobs`}>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 mb-0.5">Scheduled</p>
                        <ul className="space-y-0.5">
                          {scheduledByService[svc].map(w => (
                            <li key={w.job_type} className="flex flex-wrap items-baseline gap-x-1.5 text-xs text-grey-700">
                              <span className="font-medium">{JOB_CONFIG[w.job_type]?.label ?? w.job_type.replace(/_/g, ' ')}</span>
                              <span className="font-semibold text-amber-700">×{w.count}</span>
                              <span className="text-grey-500">
                                {w.next_due ? formatDue(w.next_due) : '—'}
                                {w.last_due && w.last_due !== w.next_due ? ` → ${formatDue(w.last_due)}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {queueLen === 0 ? (
                      <p className="text-xs text-grey-400 italic px-1">Idle</p>
                    ) : (
                      <div className="space-y-2">
                        {/* Phones cap each column at MOBILE_CAP cards so a busy
                            newsroom doesn't stack into a full-screen wall; the
                            "+N more" line absorbs the difference. lg+ keeps the
                            full PER_COLUMN_CAP sample. */}
                        {visible.map((job, i) => (
                          <div key={job.job_id} className={i >= MOBILE_CARD_CAP ? 'hidden lg:block' : undefined}>
                            <ActiveJobCard job={job} />
                          </div>
                        ))}
                        {queueLen - Math.min(visible.length, MOBILE_CARD_CAP) > 0 && (
                          <p className={`lg:hidden text-xs ${palette.text} font-semibold px-1 pt-1`}>
                            + {queueLen - Math.min(visible.length, MOBILE_CARD_CAP)} more in queue
                          </p>
                        )}
                        {overflow > 0 && (
                          <p className={`hidden lg:block text-xs ${palette.text} font-semibold px-1 pt-1`}>+ {overflow} more in queue</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* ── Heartbeat: pipeline lanes + dependency health ───── */}
      <HeartbeatStrip heartbeat={data?.heartbeat} queueDepth={data?.queue_depth?.total ?? 0} />

      {/* ── Commissions — the assignment desk (§7.5) ────────── */}
      <CommissionsCard />

      {/* ── Action Queue — uniform rows, 1-col mobile / 2-col desktop ─ */}
      <section className="min-w-0">
        <h2 className="text-sm font-semibold text-grey-900 uppercase tracking-wider mb-3">Action Queue</h2>
        <ActionQueue
          aq={data?.action_queue}
          linkedinReview={data?.linkedin_resolver?.needs_review ?? 0}
          attention={attention}
        >
          <MergeQueueAttentionStrip />
        </ActionQueue>
      </section>

      {/* ── Coverage by exchange & content path (sourcing cut) ── */}
      <CoverageByLaneCard lanes={data?.coverage_by_lane || []} />

      {/* ── F1: mandated event-class coverage by state (last 7 days) ── */}
      <MandatedCoverageCard panel={data?.mandated_coverage ?? null} />

      {/* Recent Activity — collapsed by default (debug feed, not operations) */}
      <CollapsibleActivity
        isLoading={isLoading}
        timeline={timeline}
        timelineGroups={timelineGroups}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

// Sydney business window — lanes legitimately go quiet outside it, so the
// heartbeat greys instead of alarming (schedule-aware thresholds).
function inSydneyBusinessWindow(): boolean {
  const syd = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
  const day = syd.getDay(); // 0 Sun .. 6 Sat
  const mins = syd.getHours() * 60 + syd.getMinutes();
  return day >= 1 && day <= 5 && mins >= 7 * 60 && mins <= 19 * 60 + 30;
}

const LANE_LABELS: Record<string, string> = {
  ingest: 'Ingest', generate: 'Generate', publish: 'Publish',
  promote: 'Promote', social: 'Social', notify: 'Notify',
};
// In-window ages (minutes) before amber/red per lane.
const LANE_THRESHOLDS: Record<string, [number, number]> = {
  ingest: [45, 90], generate: [90, 180], publish: [60, 150],
  promote: [90, 180], social: [180, 360], notify: [360, 720],
};

// Plain-language blast radius per dependency — states what the queue
// architecture guarantees, so an episode reads as information, not alarm.
const BLAST_RADIUS: Record<string, string> = {
  github: 'Publishing holds and retries; articles keep generating; nothing is lost.',
  buffer: 'Social pushes hold and retry; drafts stay queued; nothing posts twice.',
  linkedin: 'URN resolution pauses (breaker/budget); posts release untagged as designed.',
  mailjet: 'Notification sends fail visibly and can be resent; drafts are unaffected.',
  firecrawl: 'Discovery/sourcing stalls; monitors retry; nothing already ingested is affected.',
  marketindex: 'ASX ingestion degraded; per-company sweeps retry; the bad-render guard is holding.',
  nsx: 'NSX ingestion degraded; the 30-min cadence retries.',
  cynapse: 'Announcement processing fails and requeues; articles for affected filings are delayed, not lost.',
  gcs: 'Image storage degraded; publishes continue text-only where images fail.',
  cf_builds: 'Site builds stalled — merged content (articles included) is NOT reaching readers. Retry lever: an empty commit to the affected branch, or the CF dashboard Builds tab.',
  anthropic: 'Article and synthesis generation fails and requeues; affected filings are delayed, not lost. Fact-check and processing are unaffected.',
  gemini: 'Announcement processing/triage degraded; jobs requeue; generation of already-processed filings continues.',
  perplexity: 'Fact-checking degraded; generation continues with the fact-check marked failed for operator review.',
  // GCP-sourced (Cloud Monitoring webhook, OPERATOR_ALERTING_PLAN Phase 1)
  publishing: 'Builds/deploys and social pushes fail on the publishing service; content already live is unaffected; queued jobs retry.',
  backend: 'The API/pipeline itself is erroring — check the Heartbeat lanes and Cloud Run logs; work queues rather than being lost.',
  gateway: 'Every product behind the gateway is affected — manager, publishing webhooks, service registry. Check gateway-service first.',
};

function HeartbeatStrip({ heartbeat, queueDepth }: { heartbeat?: HeartbeatBlock; queueDepth: number }) {
  if (!heartbeat) return null;
  const inWindow = inSydneyBusinessWindow();
  // A row the sweeper does not judge (external / unused) is never "unhealthy";
  // a row with no recent evidence is never "healthy" (2026-10-06).
  const unhealthyDeps = heartbeat.dependencies.filter(
    d => d.state !== 'healthy' && d.evidence !== 'external' && d.evidence !== 'unused');
  const noEvidence = heartbeat.dependencies.filter(d => d.evidence === 'stale');
  const labelled = heartbeat.dependencies.filter(d => d.evidence === 'external' || d.evidence === 'unused');
  const openEpisodes = heartbeat.episodes.filter(e => !e.closed_at);
  // Resolved episodes: the backend sends those closed in the last hour only
  // (operator ruling 2026-10-06), shown greyed with their resolve time.
  const resolvedEpisodes = heartbeat.episodes.filter(e => !!e.closed_at);
  const agedOpen = heartbeat.episodes_open_aged ?? 0;
  const knownOpen = openEpisodes.filter(e => e.dependency !== 'unclassified');
  const unknownOpen = openEpisodes.filter(e => e.dependency === 'unclassified');
  const allQuiet = unhealthyDeps.length === 0 && openEpisodes.length === 0;

  // Provider status-page incidents (Cloudflare/GitHub/Anthropic/Mailjet) —
  // the is-it-them-or-us layer. Incidents matching an open episode or a
  // degraded dependency render INSIDE that card ("their status page
  // confirms it"); the rest render as one early-warning line, because the
  // provider often acknowledges before our own traffic trips (and vice
  // versa — on 2026-08-03 our builds hung 15 minutes before Cloudflare's
  // status page said anything).
  const platformIncidents = heartbeat.platform_status?.incidents ?? [];
  const incidentsFor = (dep: string) =>
    platformIncidents.filter(i => i.dependencies.includes(dep));
  const locallyVisibleDeps = new Set([
    ...openEpisodes.map(e => e.dependency),
    ...unhealthyDeps.map(d => d.dependency),
  ]);
  const standaloneIncidents = platformIncidents.filter(
    i => !i.dependencies.some(dep => locallyVisibleDeps.has(dep)),
  );
  const incidentLine = (i: (typeof platformIncidents)[number]) =>
    `${i.provider} — “${i.name}” (${i.status.replace(/_/g, ' ')}${
      i.started_at ? ` since ${format(new Date(i.started_at), 'HH:mm')}` : ''
    })`;

  // Social flow — demand-driven blockage detection (2026-08-04). The lane
  // chip shows freshness; THESE lines say whether posts that SHOULD have
  // gone out actually did, and name the blocked stage when not. Silent
  // when every stage flows.
  const sf = heartbeat.social_flow;
  const socialAlerts: Array<{ tone: 'warn' | 'bad'; text: string }> = [];
  if (sf) {
    if (sf.approved_unpushed > 0) {
      socialAlerts.push({ tone: 'bad', text: `${sf.approved_unpushed} approved social post${sf.approved_unpushed === 1 ? '' : 's'} never pushed to Buffer (>1h) — push worker may be stalled.` });
    }
    if (sf.failed_24h > 0) {
      socialAlerts.push({ tone: 'warn', text: `${sf.failed_24h} Buffer push failure${sf.failed_24h === 1 ? '' : 's'} in 24h${sf.latest_fail_error ? ` — latest: ${sf.latest_fail_error}` : ''}. Failed drafts can be re-pushed from Social Drafts.` });
    }
    if (sf.promoted_no_draft > 0) {
      socialAlerts.push({ tone: 'warn', text: `${sf.promoted_no_draft} article${sf.promoted_no_draft === 1 ? '' : 's'} promoted in the last 24h produced no social draft — compose stage skipped them.` });
    }
    if (sf.recent_stuck > 10) {
      socialAlerts.push({ tone: 'warn', text: `${sf.recent_stuck} posts pushed 3–24h ago still unposted — Buffer queue may be backing up.` });
    }
    // Week spread (operator ruling 2026-10-02): the backlog draining overnight
    // and at weekends is normal; posts hitting the 3-day limit are not.
    if ((sf.aged_out_backlog_24h ?? 0) > 0) {
      socialAlerts.push({ tone: 'warn', text: `${sf.aged_out_backlog_24h} social post${sf.aged_out_backlog_24h === 1 ? '' : 's'} aged out of the 3-day backlog in 24h without a Buffer slot${sf.backlog_waiting ? ` (${sf.backlog_waiting} still waiting)` : ''}.` });
    }
  }

  return (
    <section id="heartbeat" className="space-y-2">
      {/* Lane heartbeat line */}
      <div className={`rounded-xl border p-3 ${allQuiet ? 'border-success-200 bg-success-50' : 'border-grey-200 bg-surface'}`}>
        <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-xs">
          <span className={`h-2 w-2 rounded-full flex-shrink-0 ${allQuiet ? 'bg-success-500' : 'bg-warning-400'}`} />
          {Object.entries(heartbeat.lanes).map(([lane, ts]) => {
            const age = ts ? Date.now() - new Date(ts).getTime() : null;
            const mins = age != null ? Math.round(age / 60000) : null;
            const [amberAt, redAt] = LANE_THRESHOLDS[lane] ?? [120, 240];
            let cls = 'text-grey-600';
            // social + notify joined the quiet-hours grey-out 2026-08-04:
            // pushes and sends follow the publish day, so their age
            // legitimately exceeds the amber/red thresholds overnight —
            // the red lane every morning was noise, not signal.
            if (!inWindow && (lane === 'ingest' || lane === 'publish' || lane === 'promote' || lane === 'social' || lane === 'notify')) {
              cls = 'text-grey-400'; // quiet hours — expected idle
            } else if (mins == null || mins > redAt) cls = 'text-danger-600 font-semibold';
            else if (mins > amberAt) cls = 'text-warning-700 font-semibold';
            return (
              <span key={lane} className={cls}>
                {LANE_LABELS[lane] ?? lane}{' '}
                {mins == null ? '—' : mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h`}
              </span>
            );
          })}
          <span className="text-grey-400">· queue {queueDepth}</span>
          {heartbeat.deploy.backend_revision && (
            <span className="text-grey-300 ml-auto hidden sm:inline" title="Backend revision (deploy correlation)">
              {heartbeat.deploy.backend_revision.split('-').slice(-2).join('-')}
            </span>
          )}
        </div>
      </div>

      {/* Social flow blockages — demand-driven: only renders when a post
          that should have gone out didn't, with the stage named. */}
      {socialAlerts.map((a, i) => (
        <div key={i} className={`rounded-xl border px-3 py-2 text-xs ${a.tone === 'bad' ? 'border-danger-200 bg-danger-50 text-danger-800' : 'border-warning-200 bg-warning-50 text-warning-800'}`}>
          <span className="font-semibold">Social:</span> {a.text}
          {sf && (
            <span className="text-grey-500"> · 24h flow: {sf.promoted_24h} promoted → {sf.drafted_24h} drafted → {sf.pushed_24h} pushed → {sf.posted_24h} posted</span>
          )}
        </div>
      ))}

      {/* Known-dependency episodes — named, plain language */}
      {knownOpen.map(ep => (
        <div key={ep.episode_id} className="rounded-xl border border-danger-200 bg-danger-50 p-3 text-sm text-danger-800">
          <span className="font-semibold capitalize">{ep.dependency}</span>{' '}
          {ep.failure_class.replace(/_/g, ' ')} since {format(new Date(ep.opened_at), 'HH:mm')} —{' '}
          {ep.failure_count} failure{ep.failure_count === 1 ? '' : 's'}, last {timeAgo(ep.last_failure_at)}.{' '}
          {BLAST_RADIUS[ep.dependency] ?? ''}
          {incidentsFor(ep.dependency).map(i => (
            <span key={i.provider + i.name} className="block text-xs font-semibold text-danger-700 mt-0.5">
              Their side, not ours:{' '}
              <a href={i.url} target="_blank" rel="noreferrer" className="underline">
                {incidentLine(i)}
              </a>
            </span>
          ))}
          {ep.backend_revision && (
            <span className="block text-xs text-danger-600 mt-0.5">
              Revisions at open: backend {ep.backend_revision}
              {ep.publishing_revision ? ` · publishing ${ep.publishing_revision}` : ''} — compare with deploy times before suspecting a change.
            </span>
          )}
        </div>
      ))}

      {/* Unknown-signature episodes — the "never seen before" voice */}
      {unknownOpen.map(ep => (
        <div key={ep.episode_id} className="rounded-xl border border-warning-300 bg-warning-50 p-3 text-sm text-warning-800">
          <span className="font-semibold">Something new:</span> {ep.failure_count} failure{ep.failure_count === 1 ? '' : 's'} matching no
          known signature since {format(new Date(ep.opened_at), 'HH:mm')}
          {ep.affected ? <> — affected: {ep.affected}</> : null}.
          {ep.sample_error && (
            <span className="block font-mono text-xs mt-1 text-warning-700 break-all">“{ep.sample_error.slice(0, 160)}”</span>
          )}
        </div>
      ))}

      {/* Resolved in the last hour — greyed, then gone */}
      {resolvedEpisodes.map(ep => (
        <div key={ep.episode_id} className="rounded-xl border border-grey-200 bg-grey-50 px-3 py-2 text-xs text-grey-500">
          <span className="font-semibold capitalize">{ep.dependency === 'unclassified' ? 'Unclassified' : ep.dependency}</span>{' '}
          {ep.failure_class.replace(/_/g, ' ')} · resolved {format(new Date(ep.closed_at as string), 'HH:mm')}
          {' '}(opened {format(new Date(ep.opened_at), 'HH:mm')})
        </div>
      ))}

      {/* Open episodes with no failure for 7+ days: one line, not a wall */}
      {agedOpen > 0 && (
        <div className="rounded-xl border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
          {agedOpen} open &gt; 7 days (check the source) — no failure recorded in over a week, but never closed.
        </div>
      )}

      {/* Rows with no recent evidence, and rows another monitor owns — one
          grey line, so "healthy" is only ever shown when something proved it */}
      {(noEvidence.length > 0 || labelled.length > 0) && (
        <p className="px-1 text-[11px] text-grey-400">
          {noEvidence.length > 0 && (
            <>No evidence: {noEvidence.map(d => `${d.dependency} (${d.last_success_at ? `since ${format(new Date(d.last_success_at), 'd MMM')}` : 'never'})`).join(', ')}</>
          )}
          {noEvidence.length > 0 && labelled.length > 0 && ' · '}
          {labelled.map(d => `${d.dependency}: ${d.evidence_note ?? d.evidence}`).join(' · ')}
        </p>
      )}

      {/* Degraded dependencies without an open episode (recovering / low-grade) */}
      {unhealthyDeps.filter(d => !openEpisodes.some(e => e.dependency === d.dependency)).map(d => (
        <div key={d.dependency} className="rounded-xl border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
          <span className="font-semibold capitalize">{d.dependency}</span> {d.state} since {timeAgo(d.state_changed_at)}
          {d.last_success_at ? <> · last success {timeAgo(d.last_success_at)}</> : null}
          {incidentsFor(d.dependency).map(i => (
            <span key={i.provider + i.name} className="block font-semibold mt-0.5">
              Their side, not ours:{' '}
              <a href={i.url} target="_blank" rel="noreferrer" className="underline">
                {incidentLine(i)}
              </a>
            </span>
          ))}
        </div>
      ))}

      {/* Provider incidents with no local impact yet — early warning only.
          One quiet line; the point is that the operator learns "Cloudflare
          is having a builds incident" from the dashboard, not from an hour
          of ruling out our own commits. */}
      {standaloneIncidents.length > 0 && (
        <div className="rounded-xl border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
          <span className="font-semibold">Provider incident{standaloneIncidents.length === 1 ? '' : 's'} (no local impact detected yet):</span>{' '}
          {standaloneIncidents.map((i, idx) => (
            <span key={i.provider + i.name}>
              {idx > 0 ? ' · ' : ''}
              <a href={i.url} target="_blank" rel="noreferrer" className="underline">
                {incidentLine(i)}
              </a>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function AgeChip({ at }: { at: string | null }) {
  if (!at) return null;
  const mins = Math.round((Date.now() - new Date(at).getTime()) / 60000);
  const label = mins < 60 ? `${mins}m` : mins < 60 * 48 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / 1440)}d`;
  const stale = mins > 24 * 60;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${stale ? 'bg-grey-100 text-grey-500' : 'bg-warning-100 text-warning-800'}`}>
      oldest {label}
    </span>
  );
}

// One anatomy for every Action Queue row: tinted icon tile · label (+optional
// sublabel) · age chip · accent-matched count pill · chevron (link) or caret
// (expandable) or inline action. Counts never live inside labels.
const QUEUE_ACCENTS = {
  danger:  { border: 'border-danger-200',  tile: 'bg-danger-100 text-danger-600',   pill: 'bg-danger-100 text-danger-700' },
  warning: { border: 'border-warning-200', tile: 'bg-warning-100 text-warning-700', pill: 'bg-warning-100 text-warning-800' },
  info:    { border: 'border-info-200',    tile: 'bg-info-100 text-info-700',       pill: 'bg-info-100 text-info-700' },
  muted:   { border: 'border-grey-200',    tile: 'bg-grey-100 text-grey-500',       pill: 'bg-grey-100 text-grey-600' },
} as const;

function QueueRow({ to, onClick, open, icon: Icon, label, sublabel, count, extraPill, oldestAt, accent = 'muted', action, children }: {
  to?: string; onClick?: () => void; open?: boolean;
  icon: typeof FileText; label: string; sublabel?: string; count: number;
  extraPill?: React.ReactNode; oldestAt?: string | null;
  accent?: keyof typeof QUEUE_ACCENTS; action?: React.ReactNode; children?: React.ReactNode;
}) {
  if (count === 0) return null;
  const a = QUEUE_ACCENTS[accent];
  // Age/extra chips ride inline on >=sm; on phones they drop under the label
  // so the trailing cluster can't squeeze the label into vertical wrap.
  const meta = (oldestAt || extraPill) ? (
    <>
      <AgeChip at={oldestAt ?? null} />
      {extraPill}
    </>
  ) : null;
  const inner = (
    <>
      <span className={`flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${a.tile}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-grey-900 truncate">{label}</span>
        {sublabel && <span className="block text-xs text-grey-500">{sublabel}</span>}
        {meta && <span className="sm:hidden flex items-center gap-1.5 mt-1">{meta}</span>}
      </span>
      {meta && <span className="hidden sm:flex items-center gap-1.5 flex-shrink-0">{meta}</span>}
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${a.pill}`}>{count}</span>
      {to && <ArrowRight className="h-3.5 w-3.5 text-grey-300 flex-shrink-0" />}
      {onClick && (open
        ? <ChevronUp className="h-3.5 w-3.5 text-grey-400 flex-shrink-0" />
        : <ChevronDown className="h-3.5 w-3.5 text-grey-400 flex-shrink-0" />)}
    </>
  );
  return (
    <div className={`rounded-xl border ${a.border} bg-surface min-w-0`}>
      {to ? (
        <Link to={to} className="flex items-center gap-3 p-3 hover:bg-grey-50 active:bg-grey-100 transition-colors">{inner}</Link>
      ) : onClick ? (
        <button onClick={onClick} className="w-full flex items-center gap-3 p-3 text-left hover:bg-grey-50 active:bg-grey-100 transition-colors">{inner}</button>
      ) : (
        <div className="flex items-center gap-3 p-3">{inner}{action}</div>
      )}
      {children}
    </div>
  );
}

function ActionQueue({ aq, linkedinReview, attention, children }: {
  aq?: ActionQueueBlock; linkedinReview: number; attention: DashboardAttentionItem[];
  children?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [jobsOpen, setJobsOpen] = useState(false);
  if (!aq) return null;
  const failedJobItems = attention
    .filter(a => a.attention_type === 'failed_job')
    .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());
  // Build failures get their own row rather than sharing the collapsed
  // "Failed jobs" toggle: a masthead that has stopped deploying is the
  // failure this whole publishing arrangement exists to catch, and it was
  // previously indistinguishable from a failed image-enrichment job.
  const buildFailedItems = attention
    .filter(a => a.attention_type === 'build_failed')
    .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());
  // Count MASTHEADS, not rows. The query returns one row per (masthead,
  // destination), so a site failing on both Preview and Production is two rows
  // and one masthead. Before 2026-08-31 this counted rows and the query emitted
  // one per failed RUN, so Bell Wire's 55-failure outage rendered as
  // "10 mastheads have stopped deploying" — the LIMIT, not a fact about the
  // estate. Naming the single masthead is also more useful than counting to one.
  const buildFailedMastheads = Array.from(
    new Set(buildFailedItems.map(a => a.target_name).filter(Boolean) as string[]),
  );
  // Build memory approaching a ceiling (§7.5). WARNING, not danger: nothing is
  // broken yet — that is the entire point. It fires while there is still time
  // to act, unlike the two ceilings Bell Wire hit as outages on 2026-08-31.
  // One row per masthead, grouped by the query.
  const buildMemoryItems = attention
    .filter(a => a.attention_type === 'build_memory_high')
    .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());
  // Production serving an older version than preview. Grouped per masthead by
  // the query, so this is at most one row per site.
  // Coverage breaches (2026-09-17): the paid guarantee (T-3) and the mandated
  // event classes (A5). Both were computed by the backend but nothing on this
  // page rendered them until now — a breach with no bucket is a silent one.
  const coverageBreachItems = attention
    .filter(a => a.attention_type === 'coverage_guarantee_breach' || a.attention_type === 'mandated_coverage_breach');
  // Pipeline health (2026-10-05): a filing that got a second article on one
  // masthead. Expected to be empty since backend 0.3.6 — any row is a leak.
  const duplicateItems = attention
    .filter(a => a.attention_type === 'duplicate_generation');
  const promoteStaleItems = attention
    .filter(a => a.attention_type === 'promote_stale')
    .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());
  const rbTotal = aq.review_blocked_by_target.reduce((n, t) => n + t.count, 0);
  const rbPrimary = aq.review_blocked_by_target.reduce((n, t) => n + t.primary_count, 0);
  const agedTotal = aq.announcements_failed_aged + aq.jobs_failed_aged;
  // E2 — halts we are expecting an announcement from. Accountable-mode:
  // this console shows the machinery deliberately.
  const hw = aq.halt_watch;
  const hwOutstanding = hw?.outstanding ?? 0;
  const hwOverdue = hw?.overdue ?? 0;
  // "Nothing needs you" is about TODAY. Standing backlogs are deliberately not
  // part of this test (2026-09-23 ruling): they are never zero, so counting
  // them meant the reassurance could never appear.
  const empty = aq.announcements_failed_recent === 0 && aq.jobs_failed_recent === 0
    && hwOutstanding === 0 && hwOverdue === 0
    && buildFailedItems.length === 0 && promoteStaleItems.length === 0
    && buildMemoryItems.length === 0;

  const refresh = () => qc.invalidateQueries({ queryKey: ['dashboard'] });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-start">
      {empty && (
        <div className="lg:col-span-2 flex items-center gap-3 p-4 rounded-xl bg-success-50 border border-success-200">
          <CheckCircle2 className="h-5 w-5 text-success-500" />
          <span className="text-sm font-medium text-success-700">Nothing needs you</span>
        </div>
      )}
      {(hwOutstanding > 0 || hwOverdue > 0) && (
        <QueueRow
          to="/review-queue" icon={Clock} accent={hwOverdue > 0 ? 'warning' : 'info'}
          label="Halts pending an announcement"
          sublabel={hw!.items
            .slice(0, 4)
            .map(i => {
              // Mark a deadline we INFERRED so it is never mistaken for one
              // the company gave.
              const due = i.deadline ? `${i.deadline}${i.deadline_stated ? '' : '~'}` : 'no date';
              return `${i.company_code ?? '?'} ${due}`;
            })
            .join(' · ')}
          count={hwOutstanding + hwOverdue}
          oldestAt={null}
          extraPill={hwOverdue > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warning-100 text-warning-700">
              {hwOverdue} overdue
            </span>
          )}
        />
      )}
      {coverageBreachItems.length > 0 && (
        <div className="lg:col-span-2 rounded-xl border border-danger-200 bg-danger-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-danger-200">
            <AlertTriangle className="h-4 w-4 text-danger-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-danger-700">
              {coverageBreachItems.length === 1
                ? 'A coverage obligation was not met'
                : `${coverageBreachItems.length} coverage obligations not met`}
            </span>
          </div>
          <div className="divide-y divide-danger-200/60">
            {coverageBreachItems.map(item => (
              <div key={item.item_id} className="p-2.5">
                <AttentionCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}
      {duplicateItems.length > 0 && (
        <div className="lg:col-span-2 rounded-xl border border-warning-200 bg-warning-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-warning-200">
            <AlertTriangle className="h-4 w-4 text-warning-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-warning-700">
              {duplicateItems.length === 1
                ? 'Duplicate articles were generated'
                : `Duplicate articles on ${duplicateItems.length} masthead-days`}
            </span>
          </div>
          <div className="divide-y divide-warning-200/60">
            {duplicateItems.map(item => (
              <div key={item.item_id} className="p-2.5">
                <AttentionCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}
      {buildFailedItems.length > 0 && (
        <div className="lg:col-span-2 rounded-xl border border-danger-200 bg-danger-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-danger-200">
            <AlertTriangle className="h-4 w-4 text-danger-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-danger-700">
              {buildFailedItems.length === 1
                ? 'A masthead has stopped deploying'
                : buildFailedMastheads.length === 1
                  ? `${buildFailedMastheads[0]} has stopped deploying`
                  : `${buildFailedMastheads.length} mastheads have stopped deploying`}
            </span>
          </div>
          <div className="divide-y divide-danger-200/60">
            {buildFailedItems.map(item => (
              <div key={item.item_id} className="p-2.5">
                <AttentionCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}
      {buildMemoryItems.length > 0 && (
        <div className="lg:col-span-2 rounded-xl border border-warning-200 bg-warning-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-warning-200">
            <AlertTriangle className="h-4 w-4 text-warning-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-warning-700">
              {buildMemoryItems.length === 1
                ? `${buildMemoryItems[0].target_name} build memory is close to its ceiling`
                : `${buildMemoryItems.length} mastheads are close to a build memory ceiling`}
            </span>
          </div>
          <div className="divide-y divide-warning-200/60">
            {buildMemoryItems.map(item => (
              <div key={item.item_id} className="p-2.5">
                <AttentionCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}
      {promoteStaleItems.length > 0 && (
        <div className="lg:col-span-2 rounded-xl border border-warning-200 bg-warning-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-warning-200">
            <Clock className="h-4 w-4 text-warning-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-warning-800">
              Production is behind preview
            </span>
          </div>
          <div className="divide-y divide-warning-200/60">
            {promoteStaleItems.map(item => (
              <div key={item.item_id} className="p-2.5">
                <AttentionCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}
      <QueueRow
        to="/announcements?status=failed" icon={AlertTriangle} accent="danger"
        label="Failed announcements (72h)"
        count={aq.announcements_failed_recent} oldestAt={aq.announcements_failed_oldest_at}
      />
      <QueueRow
        onClick={() => setJobsOpen(v => !v)} open={jobsOpen}
        icon={XCircle} accent="danger"
        label="Failed jobs (72h)"
        count={aq.jobs_failed_recent} oldestAt={aq.jobs_failed_oldest_at}
      >
        {jobsOpen && (
          <div className="border-t border-grey-100 divide-y divide-grey-100">
            {failedJobItems.map((item, i) => (
              <div key={`${item.item_id}-${i}`} className="p-2.5">
                <AttentionCard item={item} />
                <div className="flex gap-2 mt-1.5 pl-1">
                  <button
                    disabled={busy}
                    onClick={async () => { setBusy(true); try { await retryFailedJobs([item.item_id]); refresh(); } finally { setBusy(false); } }}
                    className="touch-target px-4 py-2.5 text-xs font-medium rounded border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50"
                  >Retry</button>
                  <button
                    disabled={busy}
                    onClick={async () => { setBusy(true); try { await archiveFailedJobs([item.item_id]); refresh(); } finally { setBusy(false); } }}
                    className="touch-target px-4 py-2.5 text-xs font-medium rounded border border-grey-300 text-grey-500 hover:bg-grey-50 disabled:opacity-50"
                  >Archive</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </QueueRow>
      {children}
      {/* STANDING BACKLOGS — one line, 2026-09-23 operator ruling.
          These four run to the hundreds or thousands, are never zero, and are
          never cleared in a day, so as full rows they crowded out what
          actually changes — and this dashboard is for "what needs me now".
          Deliberately no figures quoted here: they drift daily, and a stale
          number in a comment is worse than none. Nothing is hidden — each
          count still deep-links to the page where that work is really done,
          the breakdowns survive as hover detail, and the aged-failures
          housekeeping keeps its action. */}
      <BacklogLine
        items={[
          { label: 'review-blocked', count: rbTotal, to: '/review-queue',
            pill: rbPrimary > 0 ? `${rbPrimary}P` : undefined,
            title: aq.review_blocked_by_target
              .map(t => `${t.target_name} ${t.count}${t.primary_count ? ` (${t.primary_count}P)` : ''}`)
              .join(' · ') },
          { label: 'notifications', count: aq.notifications_pending, to: '/notifications/queue' },
          { label: 'LinkedIn URNs', count: linkedinReview, to: '/review-queue?tab=linkedin' },
          { label: 'aged failures', count: agedTotal, to: '/announcements?status=failed',
            title: `${aq.announcements_failed_aged} announcements · ${aq.jobs_failed_aged} jobs` },
        ]}
        action={agedTotal > 0 && (
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await archiveFailedAnnouncements({ olderThanHours: 72 }); refresh(); } finally { setBusy(false); }
            }}
            className="touch-target px-3 py-1.5 text-xs font-medium rounded border border-grey-300 text-grey-600 hover:bg-grey-50 disabled:opacity-50 flex-shrink-0"
            title="Stamp [ARCHIVED] on announcement failures older than 72h (jobs archive individually from the failed-jobs list)"
          >Archive aged</button>
        )}
      />
    </div>
  );
}

function CollapsibleActivity({ isLoading, timeline, timelineGroups }: {
  isLoading: boolean;
  timeline: DashboardJob[];
  timelineGroups: { label: string; jobs: DashboardJob[] }[];
}) {
  // Debug feed, not operations — collapsed by default everywhere; the
  // heartbeat + action queue carry the operational signal.
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 text-left mb-3"
      >
        <h2 className="text-sm font-semibold text-grey-900 uppercase tracking-wider">Recent Activity</h2>
        {timeline.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-grey-100 text-grey-500">{timeline.length}</span>
        )}
        <span className="text-xs text-grey-400 ml-auto">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-grey-400" /></div>
        ) : timeline.length === 0 ? (
          <p className="text-sm text-grey-400 py-4">No recent activity.</p>
        ) : (
          <div className="space-y-4">
            {timelineGroups.map(group => (
              <div key={group.label}>
                <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-1.5 pl-1">{group.label}</div>
                <div className="rounded-xl border border-grey-200 bg-surface divide-y divide-grey-100 overflow-hidden">
                  {group.jobs.map(job => <TimelineRow key={job.job_id} job={job} />)}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}


function StatCard({ icon: Icon, label, value, sub, accent, link }: {
  icon: typeof FileText; label: string; value?: number; sub?: string;
  accent?: 'success' | 'danger' | 'info'; link?: string;
}) {
  const borderColor = accent === 'success' ? 'border-l-success-500'
    : accent === 'danger' ? 'border-l-danger-500'
    : accent === 'info' ? 'border-l-info-500'
    : 'border-l-grey-300';
  const iconColor = accent === 'success' ? 'text-success-500'
    : accent === 'danger' ? 'text-danger-500'
    : accent === 'info' ? 'text-info-500'
    : 'text-grey-400';

  const card = (
    <div className={`bg-surface rounded-xl border border-grey-200 border-l-4 ${borderColor} p-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-2xl font-bold text-grey-900">{value ?? <span className="text-grey-300">&mdash;</span>}</p>
          <p className="text-xs font-medium text-grey-500 mt-0.5">{label}</p>
        </div>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      {sub && <p className="text-xs text-grey-400 mt-2">{sub}</p>}
    </div>
  );

  return link ? <Link to={link}>{card}</Link> : card;
}

// Buffer API + channel budget (2026-08-07, replaces the URN resolver card —
// URN health lives on in the review queue row). Headline = the tightest
// API window's consumption; sub-lines show all windows and each channel's
// accepted pushes in the rolling last 24h vs the cap (socials S1, 2026-10-02). The scarce resource as markets/channels grow:
// Buffer limits are per API key (Team: 100/15m, 500/24h, 15k/30d).
function BufferBudgetCard({ budget }: { budget?: BufferBudget }) {
  const windows = budget?.windows ?? [];
  const pct = (w: { quota: number | null; remaining: number | null }) =>
    w.quota && w.remaining != null ? Math.round(((w.quota - w.remaining) / w.quota) * 100) : null;
  const known = windows.filter((w) => pct(w) != null);
  const worst = known.length > 0 ? Math.max(...known.map((w) => pct(w) as number)) : null;

  const accent = worst != null && worst >= 90 ? 'border-l-danger-500'
    : worst != null && worst >= 70 ? 'border-l-warning-500'
    : worst != null ? 'border-l-success-500'
    : 'border-l-grey-300';
  const iconColor = worst != null && worst >= 90 ? 'text-danger-500'
    : worst != null && worst >= 70 ? 'text-warning-500'
    : worst != null ? 'text-success-500'
    : 'text-grey-400';

  const windowLine = known.length > 0
    ? known.map((w) => `${w.label} ${(w.quota ?? 0) - (w.remaining ?? 0)}/${w.quota}`).join(' \u00b7 ')
    : 'awaiting first capture';

  const channels = budget?.channels ?? [];
  const cap = budget?.daily_cap_enabled ? budget?.daily_cap_per_site : null;
  const channelLine = channels.length > 0
    ? channels
        .map((c) => `${(c.renderer ?? '?').slice(0, 2).toUpperCase()}${c.platform === 'twitter' ? '/x' : ''} ${c.pushed_24h ?? c.pushed_today}${cap ? `/${cap}` : ''}`)
        .join(' \u00b7 ')
    : 'no pushes in 24h';

  return (
    <Link to="/social">
      <div className={`bg-surface rounded-xl border border-grey-200 border-l-4 ${accent} p-4 hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl font-bold text-grey-900">
              {worst != null ? `${worst}%` : <span className="text-grey-300">&mdash;</span>}
            </p>
            <p className="text-xs font-medium text-grey-500 mt-0.5">Buffer budget (worst window)</p>
          </div>
          <Zap className={`h-5 w-5 ${iconColor}`} />
        </div>
        <p className="text-xs text-grey-400 mt-2">API: {windowLine}</p>
        <p className="text-xs text-grey-400 mt-0.5">Pushes (last 24h): {channelLine}</p>
      </div>
    </Link>
  );
}

// Firecrawl credit budget (2026-08-10, Buffer-card sibling). Everything is
// LIVE from Firecrawl's /team/credit-usage — plan size, remaining and the
// billing-period reset date — so a plan change (e.g. 100k → 130k) shows up
// automatically on the next refresh, nothing hardcoded here.
function FirecrawlCreditsCard({ budget }: { budget?: FirecrawlBudget | null }) {
  const plan = budget?.plan_credits ?? null;
  const consumed = budget?.consumed_credits ?? null;
  const remaining = budget?.remaining_credits ?? null;
  const pct = plan && consumed != null ? Math.round((consumed / plan) * 100) : null;

  // Over plan is INFORMATION, not an alarm (Steve 2026-10-06): the account
  // tops up pay-as-you-go when the balance runs out. Amber, never red.
  const overPlan = !!(budget?.over_plan ?? budget?.alert);
  const accent = overPlan || (pct != null && pct >= 75) ? 'border-l-warning-500'
    : pct != null ? 'border-l-success-500'
    : 'border-l-grey-300';
  const iconColor = overPlan || (pct != null && pct >= 75) ? 'text-warning-500'
    : pct != null ? 'text-success-500'
    : 'text-grey-400';

  const fmt = (n: number | null) => (n != null ? n.toLocaleString() : '?');
  const projectionLine = budget?.projected_at_reset != null && plan != null
    ? `on pace for ${fmt(budget.projected_at_reset)} by reset (${fmt(budget.rate_per_day ?? null)}/day, ${budget.rate_basis})`
    : null;
  const usageLine = plan != null
    ? `${fmt(consumed)} / ${fmt(plan)} used · ${fmt(remaining)} left`
    : 'awaiting first fetch';
  const resetLine = budget?.billing_period_end
    ? `resets ${format(new Date(budget.billing_period_end), 'd MMM')}`
    : null;

  return (
    <div className={`bg-surface rounded-xl border border-grey-200 border-l-4 ${accent} p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-2xl font-bold text-grey-900">
            {pct != null ? `${pct}%` : <span className="text-grey-300">&mdash;</span>}
          </p>
          <p className="text-xs font-medium text-grey-500 mt-0.5">Firecrawl credits (cycle)</p>
        </div>
        <Flame className={`h-5 w-5 ${iconColor}`} />
      </div>
      <p className="text-xs text-grey-400 mt-2">{usageLine}</p>
      {resetLine && <p className="text-xs text-grey-400 mt-0.5">{resetLine}</p>}
      {projectionLine && (
        <p className={`text-xs mt-0.5 ${overPlan ? 'text-warning-700 font-semibold' : 'text-grey-400'}`}>
          {projectionLine}{overPlan ? ` — ${fmt(budget?.over_by ?? null)} over plan` : ''}
        </p>
      )}
      {overPlan && (budget?.payg_increments ?? 0) > 0 && (
        <p className="text-xs text-warning-700 mt-0.5">
          ≈ {budget!.payg_increments} top-up{budget!.payg_increments === 1 ? '' : 's'} of {fmt(budget!.payg_credits_per_increment ?? 2000)} ·
          ≈ A${(budget!.payg_cost_aud_approx ?? 0).toLocaleString()} (US${(budget!.payg_cost_usd ?? 0).toLocaleString()})
        </p>
      )}
      {budget?.next_period_forecast != null && (
        <p className="text-xs text-grey-400 mt-0.5">
          next period at this rate: {fmt(budget.next_period_forecast)}
          {(budget.next_period_over_by ?? 0) > 0 ? ` (${fmt(budget.next_period_over_by ?? 0)} over plan)` : ' (within plan)'}
        </p>
      )}
    </div>
  );
}

// LinkedIn API quota + tagging pool (2026-09-20, Buffer/Firecrawl sibling;
// operator: "on the dashboard when LinkedIn is connected"). Headline = the
// busiest endpoint's share of its per-day cap (UTC reset); LinkedIn sends no
// quota headers, so the backend's ledger is the only counter. Links to the
// connection page, which carries the full per-endpoint table.
function LinkedInApiCard({ budget }: { budget: LinkedInApiBudget }) {
  const keys = budget.keys ?? [];
  const known = keys.filter((k) => k.pct != null);
  const worst = known.length > 0 ? Math.max(...known.map((k) => k.pct as number)) : null;
  const any429 = keys.some((k) => k.n_429 > 0);
  const rolesOk = (budget.mastheads_administered ?? 0) === (budget.mastheads_total ?? 0);
  const reconsentSoon = budget.reconsent_due_in_days != null && budget.reconsent_due_in_days <= 30;

  const accent = any429 || !rolesOk ? 'border-l-danger-500'
    : (worst != null && worst >= 90) || reconsentSoon ? 'border-l-warning-500'
    : worst != null ? 'border-l-success-500'
    : 'border-l-grey-300';
  const iconColor = any429 || !rolesOk ? 'text-danger-500'
    : (worst != null && worst >= 90) || reconsentSoon ? 'text-warning-500'
    : worst != null ? 'text-success-500'
    : 'text-grey-400';

  const short = (k: string) => k
    .replace('organizations-FINDER', 'finder')
    .replace('organizationsLookup-BATCH_GET', 'batch')
    .replace('organizationAcls-FINDER', 'roles')
    .replace('networkSizes-GET', 'followers')
    .replace('organizationalEntityFollowerStatistics-FINDER', 'follower-stats')
    .replace('organizationalEntityShareStatistics-FINDER', 'share-stats')
    .replace('posts-CREATE', 'posts')
    .replace('images-ACTION', 'images');
  const quotaLine = keys.length > 0
    ? keys.map((k) => `${short(k.endpoint_key)} ${k.used_today}${k.cap != null ? `/${k.cap}` : ''}${k.n_429 > 0 ? ' (429)' : ''}`).join(' \u00b7 ')
    : 'no calls today';
  const poolLine = [
    `taggable ${(budget.taggable_total ?? 0).toLocaleString()}`,
    `resolved 24h ${budget.resolved_24h ?? 0}`,
    (budget.recheck_pending ?? 0) > 0 ? `recheck ${budget.recheck_pending}` : null,
    rolesOk ? null : `roles ${budget.mastheads_administered}/${budget.mastheads_total}`,
    budget.reconsent_due_in_days != null ? `re-consent ${budget.reconsent_due_in_days}d` : `token ${budget.access_expires_in_days}d`,
  ].filter(Boolean).join(' \u00b7 ');

  return (
    <Link to="/settings/linkedin">
      <div className={`bg-surface rounded-xl border border-grey-200 border-l-4 ${accent} p-4 hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl font-bold text-grey-900">
              {worst != null ? `${worst}%` : <span className="text-grey-300">&mdash;</span>}
            </p>
            <p className="text-xs font-medium text-grey-500 mt-0.5">LinkedIn API (busiest endpoint, UTC day)</p>
          </div>
          <Link2 className={`h-5 w-5 ${iconColor}`} />
        </div>
        <p className="text-xs text-grey-400 mt-2">Quota: {quotaLine}</p>
        <p className="text-xs text-grey-400 mt-0.5">Tags: {poolLine}</p>
      </div>
    </Link>
  );
}

// Live + test shortcut links per masthead. Live = egress_targets
// config.publish_domain.
//
// `test` is OURS now, not Cloudflare's. It was the Workers Builds preview of
// the `test` branch; since 2026-08-26 it is the preview alias our own build
// publishes with `wrangler versions upload --preview-alias test`. Same URL,
// different owner — and worth knowing when one looks stale, because nothing
// rebuilds it on a git push any more.
//
// The `test-` prefix is an ALIAS on the masthead's worker, not a separate
// worker script (reference_vyklow_publication_workers). Verified against the
// repos/DB 2026-08-03 — if a domain or worker name changes, this map follows.
const SITE_LINKS: Record<string, { live: string; test: string }> = {
  orecast:     { live: 'https://orecast.news',        test: 'https://test-orecast.david-18c.workers.dev' },
  bellwire:    { live: 'https://bellwire.news',       test: 'https://test-bellwire.david-18c.workers.dev' },
  marketfloor: { live: 'https://themarketfloor.news', test: 'https://test-themarketfloor.david-18c.workers.dev' },
  thepick:     { live: 'https://the-pick.com.au',     test: 'https://test-thepick.david-18c.workers.dev' },
};

// Coverage by SOURCING lane — exchange (ASX/NSX/CXA/TSX/Other/Unclassified,
// announcement-sourced) or content path (Research/Synthesis). Orthogonal to
// the per-masthead editorial cut below: this answers "which lane did content
// come THROUGH" as the network diversifies onto NSX/CXA/TSX. Tracked lanes
// render even at 0-3 (visibility of nascent lanes is the point); Unclassified
// doubles as a data-quality signal. Article counts are the LIVE (non-archived)
// corpus, so they intentionally sum lower than the all-time "Articles" card.
function MandatedCoverageCard({ panel }: { panel: MandatedCoveragePanel | null }) {
  if (!panel || panel.obligations === 0) return null;
  const pct = (n: number) => `${Math.round((n / Math.max(1, panel.obligations)) * 100)}%`;
  const age = (m: number | null) =>
    m === null ? '' : m < 60 ? `${m}m` : m < 60 * 24 ? `${Math.round(m / 60)}h` : `${Math.round(m / 60 / 24)}d`;
  const reasonLabel: Record<string, string> = {
    generation_failed: 'generation failed',
    plan_failed: 'plan failed',
    planner_declined: 'planner declined',
    no_plan: 'no plan',
    unknown: 'uncovered',
  };
  const cls = (s: string) => s.replace(/_/g, ' ');
  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-4">
      <div className="flex items-baseline justify-between mb-2 px-3 gap-3">
        <p className="font-bold text-grey-900">Mandated coverage</p>
        <p className="text-xs text-grey-400">
          {panel.obligations} obligations · last {panel.days}d · SLA {panel.sla_minutes}m
          {panel.superseded > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-grey-100 text-grey-600" title="Declarations the planner later withdrew — still obligations">
              {panel.superseded} withdrawn
            </span>
          )}
        </p>
      </div>
      {/* Three states, one bar: written · pending · breached */}
      <div className="px-3 mb-3">
        <div className="flex h-2 rounded-full overflow-hidden bg-grey-100">
          <span className="h-full bg-success-500" style={{ width: pct(panel.written) }} />
          <span className="h-full bg-warning-400" style={{ width: pct(panel.pending) }} />
          <span className="h-full bg-danger-500" style={{ width: pct(panel.breached) }} />
        </div>
        <div className="flex gap-4 mt-1.5 text-xs">
          <span className="text-success-700"><span className="font-semibold">{panel.written}</span> written</span>
          <span className="text-warning-700"><span className="font-semibold">{panel.pending}</span> pending</span>
          <span className="text-danger-700"><span className="font-semibold">{panel.breached}</span> breached</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 px-3 mb-2">
        <div className="divide-y divide-grey-100">
          {panel.by_target.map(b => (
            <div key={b.target_name} className="grid grid-cols-[6rem_1fr_auto] items-center gap-2 py-1.5 text-xs">
              <span className="font-semibold text-grey-800 truncate">{b.target_name}</span>
              <span className="block h-1.5 rounded-full overflow-hidden bg-grey-100">
                <span className="block h-full bg-success-500" style={{ width: `${Math.round((b.written / Math.max(1, b.obligations)) * 100)}%` }} />
              </span>
              <span className="text-grey-500 whitespace-nowrap">
                {b.written}/{b.obligations}
                {b.breached > 0 && <span className="text-danger-600"> · {b.breached} breached</span>}
              </span>
            </div>
          ))}
        </div>
        <div className="divide-y divide-grey-100">
          {panel.by_class.map(b => (
            <div key={b.activity_sub_type} className="grid grid-cols-[8rem_1fr_auto] items-center gap-2 py-1.5 text-xs">
              <span className="font-semibold text-grey-800 truncate">{cls(b.activity_sub_type)}</span>
              <span className="block h-1.5 rounded-full overflow-hidden bg-grey-100">
                <span className="block h-full bg-success-500" style={{ width: `${Math.round((b.written / Math.max(1, b.obligations)) * 100)}%` }} />
              </span>
              <span className="text-grey-500 whitespace-nowrap">
                {b.written}/{b.obligations}
                {b.breached > 0 && <span className="text-danger-600"> · {b.breached} breached</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
      {panel.items.length > 0 && (
        <div className="divide-y divide-grey-100 border-t border-grey-100">
          {panel.items.map(i => (
            <Link key={`${i.announcement_id}:${i.target_name}`} to={`/announcements/${i.announcement_id}`}
                  className="grid grid-cols-[4rem_1fr_auto] items-center gap-3 px-3 py-2 hover:bg-grey-50 transition-colors">
              <span className="text-sm font-semibold text-grey-800 truncate">{i.company_code ?? '?'}</span>
              <span className="min-w-0">
                <span className="block text-sm text-grey-800 truncate">{i.title}</span>
                <span className="block text-xs text-grey-400 truncate">
                  {i.target_name} · {cls(i.activity_sub_type ?? 'unclassified')}
                  {i.superseded && ' · withdrawn'}
                </span>
              </span>
              <span className="text-xs whitespace-nowrap text-right">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${i.state === 'breached' ? 'bg-danger-100 text-danger-700' : 'bg-warning-100 text-warning-700'}`}>
                  {i.state === 'breached' ? (reasonLabel[i.reason ?? 'unknown'] ?? i.reason) : (i.reason === null ? 'in flight' : 'pending')}
                </span>
                <span className="text-grey-400 ml-2">{age(i.age_minutes)}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageByLaneCard({ lanes }: { lanes: CoverageLaneRow[] }) {
  if (lanes.length === 0) return null;
  const nf = (n: number) => n.toLocaleString();
  // Bar scale: proportional to the largest exchange lane's announcement total
  // (ASX dominates, so alt-lanes render as honest slivers).
  const maxAnn = Math.max(
    1,
    ...lanes.filter((l) => l.kind === 'exchange').map((l) => l.announcements_total),
  );
  // Exchange rows drill into the announcements list (backend exchange filter);
  // 'Other/Intl' is the untracked complement with no single filter value, so
  // it is not a link. Path rows drill into the Articles gallery's existing
  // native lanes: 'sitewide' = synthesis, 'direct_input' = research/briefs.
  const laneLink = (l: CoverageLaneRow): string | null =>
    l.kind === 'exchange'
      ? (l.key === 'Unclassified'
          ? '/announcements?exchange=__unclassified__'
          : l.key === 'Other/Intl'
            ? null
            : `/announcements?exchange=${encodeURIComponent(l.key)}`)
      : l.key === 'Synthesis'
        ? '/articles?lane=sitewide'
        : '/articles?lane=direct_input';

  const exchangeLanes = lanes.filter((l) => l.kind === 'exchange');
  const pathLanes = lanes.filter((l) => l.kind === 'path');

  const Row = ({ l }: { l: CoverageLaneRow }) => {
    const warn = l.key === 'Unclassified' && l.announcements_total > 0;
    const barPct = l.kind === 'exchange'
      ? Math.max(l.announcements_total > 0 ? 2 : 0, Math.round((l.announcements_total / maxAnn) * 100))
      : 0;
    const to = laneLink(l);
    const rowCls = 'grid grid-cols-[7rem_1fr_auto] items-center gap-3 px-3 py-2 rounded-lg';
    const inner = (
      <>
        <span className={`text-sm font-semibold min-w-0 truncate ${warn ? 'text-warning-600' : 'text-grey-800'}`}>
          {warn && <span className="mr-1">⚠</span>}{l.key}
        </span>
        <span className="min-w-0">
          {l.kind === 'exchange' ? (
            <span className="block h-2 bg-grey-100 rounded-full overflow-hidden">
              <span className="block h-full bg-primary-500 rounded-full" style={{ width: `${barPct}%` }} />
            </span>
          ) : (
            <span className="text-xs text-grey-400">
              {l.key === 'Synthesis' ? 'non-announcement wraps' : 'research uploads'}
            </span>
          )}
        </span>
        <span className="text-xs text-grey-500 whitespace-nowrap text-right">
          {l.kind === 'exchange' ? (
            <>
              <span className="text-grey-700 font-medium">{nf(l.announcements_total)}</span> ann
              {l.announcements_today > 0 && <span className="text-success-600"> (+{l.announcements_today})</span>}
              <span className="text-grey-300"> · </span>
              {nf(l.articles_total)} art
            </>
          ) : (
            <>
              <span className="text-grey-700 font-medium">{nf(l.articles_total)}</span> art
              <span className="text-grey-300"> · </span>
              {nf(l.articles_published)} pub
              {l.articles_today > 0 && <span className="text-success-600"> (+{l.articles_today})</span>}
            </>
          )}
        </span>
      </>
    );
    return to ? (
      <Link to={to} className={`${rowCls} hover:bg-grey-50 transition-colors`}>{inner}</Link>
    ) : (
      <div className={rowCls}>{inner}</div>
    );
  };

  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-4">
      <div className="flex items-baseline justify-between mb-2 px-3">
        <p className="font-bold text-grey-900">Coverage by exchange &amp; path</p>
        <p className="text-xs text-grey-400">announcements · articles (today deltas)</p>
      </div>
      <div className="divide-y divide-grey-100">
        {exchangeLanes.map((l) => <Row key={l.key} l={l} />)}
      </div>
      {pathLanes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-grey-200 divide-y divide-grey-100">
          {pathLanes.map((l) => <Row key={l.key} l={l} />)}
        </div>
      )}
    </div>
  );
}

// Capacity queue (Steve 2026-11-02): one masthead's slice of each half. A
// half the backend could not read, or an older backend that sends no
// capacity_queue, is 'unavailable' — never shown as zeros.
export type CapacitySlice<T> =
  | { kind: 'unavailable'; reason: string }
  | { kind: 'off' }
  | { kind: 'none' }
  | { kind: 'ok'; value: T };

export function capsSlice(cq: CapacityQueueBlock | undefined, site: string): CapacitySlice<MastheadCapSnapshot> {
  if (!cq || cq.caps === null) {
    return { kind: 'unavailable', reason: cq?.unavailable.find((u) => u.part === 'caps')?.reason ?? 'not reported' };
  }
  if (cq.caps.length === 0) return { kind: 'off' };
  const snap = cq.caps.find((c) => c.target_name === site);
  return snap ? { kind: 'ok', value: snap } : { kind: 'none' };
}

export function socialsSlice(cq: CapacityQueueBlock | undefined, site: string): CapacitySlice<SocialCapacity['sites'][string]> {
  if (!cq || cq.socials === null) {
    return { kind: 'unavailable', reason: cq?.unavailable.find((u) => u.part === 'socials')?.reason ?? 'not reported' };
  }
  const s = cq.socials.sites[site];
  return s ? { kind: 'ok', value: s } : { kind: 'none' };
}

function CapacityBlock({ slice }: { slice: CapacitySlice<MastheadCapSnapshot> }) {
  if (slice.kind === 'unavailable') return <p className="text-xs text-grey-400">Caps: unavailable ({slice.reason})</p>;
  if (slice.kind === 'off') return <p className="text-xs text-grey-400">Caps: off</p>;
  if (slice.kind === 'none') return <p className="text-xs text-grey-400">Caps: no snapshot for this masthead</p>;
  const c = slice.value;
  const held = c.queue.held;
  return (
    <div className="text-xs text-grey-500 space-y-0.5">
      <p>
        <span className="font-semibold text-grey-700">Caps</span>
        {c.mode === 'shadow' && <span className="text-grey-400"> (shadow)</span>}
        {': '}base {c.base.published} / {c.base.allowance_now.toFixed(1)} due (target {c.base.target})
        {' · '}leeway {c.leeway.used}/{c.leeway.size}
        {' · '}{c.exempt.total} exempt
      </p>
      <p>
        Queue {held.length}/{c.queue.limit} held · {c.queue.released} released · {c.queue.evicted} evicted
        {' · '}{c.queue.expired} expired · {c.skipped} skipped
      </p>
      {c.week && (
        <p>Week {c.week.used}/{c.week.budget} · today {c.week.today_used} of {c.week.today_share.toFixed(1)} share</p>
      )}
      {held.length > 0 && (
        <details>
          <summary className="cursor-pointer text-grey-400 hover:text-grey-600">Held ({held.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {held.map((h) => (
              <li key={h.announcement_id} className="truncate">
                <span className="font-mono">{h.company_code ?? '—'}</span> {h.title ?? '(untitled)'}
                <span className="text-grey-400"> · merit {h.merit.toFixed(2)} · {timeAgo(h.held_since)}{h.generated ? '' : ' · not generated'}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function SocialsBlock({ slice }: { slice: CapacitySlice<SocialCapacity['sites'][string]> }) {
  if (slice.kind === 'unavailable') return <p className="text-xs text-grey-400">Socials: unavailable ({slice.reason})</p>;
  if (slice.kind !== 'ok') return <p className="text-xs text-grey-400">Socials: no capacity for this masthead</p>;
  // CS567: bars on a fixed 0–50 scale with the operator cap as a target marker (manager-app).
  return <SocialPostingBars platforms={slice.value.platforms} />;
}

// Per-site routing pacing (live vs daily target) + resource/non-resource
// sector mix (14d). The operator's day-to-day view of the marketfloor
// coverage problem — replaces the old vanity total cards.
function SitePerformanceStrip({ bySite, bySector, capacity }: {
  bySite: ArticlesBySiteRow[];
  bySector: ArticlesBySectorRow[];
  capacity?: CapacityQueueBlock;
}) {
  // Stable display order; drop the 'unrouted' bucket from the headline view.
  const ORDER = ['orecast', 'bellwire', 'marketfloor', 'thepick'];
  const sites = [...bySite]
    .filter((s) => s.target_name !== 'unrouted')
    .sort((a, b) => ORDER.indexOf(a.target_name) - ORDER.indexOf(b.target_name));
  const sectorByTarget = new Map(bySector.map((r) => [r.target_name, r]));
  if (sites.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {sites.map((s) => {
        const target = s.daily_target || 0;
        const pct = target > 0 ? Math.min(100, Math.round((s.live_today / target) * 100)) : 0;
        const behind = target > 0 && s.live_today < target;
        const sec = sectorByTarget.get(s.target_name);
        const res = sec?.resource_14d ?? 0;
        const non = sec?.nonresource_14d ?? 0;
        const secTotal = res + non;
        const nonPct = secTotal > 0 ? Math.round((non / secTotal) * 100) : 0;
        const links = SITE_LINKS[s.target_name];
        return (
          <div key={s.target_name} className="bg-surface rounded-xl border border-grey-200 p-4">
            <div className="flex items-baseline justify-between">
              <span className="flex items-baseline gap-2 min-w-0">
                <p className="font-bold text-grey-900 capitalize">{s.target_name}</p>
                {links && (
                  <span className="text-xs whitespace-nowrap">
                    <a href={links.live} target="_blank" rel="noopener noreferrer"
                      className="text-grey-400 hover:text-primary-600 hover:underline">
                      live<ExternalLink className="inline h-3 w-3 ml-0.5 align-[-1px]" />
                    </a>
                    <span className="text-grey-300"> · </span>
                    <a href={links.test} target="_blank" rel="noopener noreferrer"
                      className="text-grey-400 hover:text-primary-600 hover:underline">
                      test<ExternalLink className="inline h-3 w-3 ml-0.5 align-[-1px]" />
                    </a>
                  </span>
                )}
              </span>
              <p className="text-sm text-grey-500">
                <span className={`font-semibold ${behind ? 'text-warning-600' : 'text-success-600'}`}>
                  {s.live_today}
                </span>
                <span className="text-grey-400"> / {target} live today</span>
              </p>
            </div>
            {/* pacing bar */}
            <div className="h-2 bg-grey-100 rounded-full mt-2 overflow-hidden">
              <div
                className={`h-full rounded-full ${behind ? 'bg-warning-400' : 'bg-success-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-grey-400 mt-1">{s.generated_today} generated today</p>
            {/* resource vs non-resource mix (14d) */}
            {secTotal > 0 && (
              <div className="mt-3">
                <div className="flex h-2 rounded-full overflow-hidden">
                  <div className="bg-amber-400" style={{ width: `${100 - nonPct}%` }} title={`Resource ${res}`} />
                  <div className="bg-sky-500" style={{ width: `${nonPct}%` }} title={`Non-resource ${non}`} />
                </div>
                <p className="text-xs text-grey-400 mt-1">
                  14d mix: {res} resource · {non} non-resource ({nonPct}% non-resource)
                </p>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-grey-100 space-y-2">
              <CapacityBlock slice={capsSlice(capacity, s.target_name)} />
              <SocialsBlock slice={socialsSlice(capacity, s.target_name)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NavPill({ to, icon: Icon, label, danger }: {
  to: string; icon: typeof FileText; label: string; danger?: boolean;
}) {
  return (
    <Link to={to} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
      danger
        ? 'bg-danger-100 text-danger-700 hover:bg-danger-200 border border-danger-200'
        : 'bg-grey-100 text-grey-600 hover:bg-grey-200 border border-grey-200'
    }`}>
      <Icon className="h-3 w-3" /> {label}
    </Link>
  );
}

function ActiveJobCard({ job }: { job: DashboardJob }) {
  const cfg = JOB_CONFIG[job.job_type] || JOB_CONFIG.process;
  const svc = jobService(job.job_type);
  const palette = SERVICE_COLORS[svc];

  // Subtitle pieces: stage label \u00b7 entity \u00b7 \u2192 target \u00b7 status. Build from
  // the parts that exist so we never show stray separators.
  const code = job.company_code;
  const codeLabel = code
    ? (['PRIVATE', 'OTC', 'OTCQX', 'OTCQB', 'PINK'].includes(code) ? 'Research' : code)
    : null;
  const target = (job.payload as any)?.target as string | undefined;
  const subtitleParts = [
    cfg.label,
    codeLabel,
    target ? `\u2192 ${target}` : null,
    statusLabel(job.status),
  ].filter(Boolean);

  // A waiting job is parked on a timer (delayed dispatch / retry backoff),
  // not in flight \u2014 show a still clock + its due time, not a spinner + age.
  const waiting = job.waiting && !!job.next_run_at;
  const dueLabel = waiting && job.next_run_at ? `due ${formatDue(job.next_run_at)}` : null;
  const time = waiting ? dueLabel : timeAgo(job.created_at);

  // Retry reason. The worker writes it to error_message on every failed
  // attempt (and to result.last_error); show it the moment a job is
  // retrying — not only once it has burned all attempts and gone terminal.
  const retryReason = job.status === 'retrying' || (job.attempts > 0 && job.status === 'pending')
    ? (job.error_message || (job.result as any)?.last_error || null)
    : null;
  const attemptLabel = job.max_attempts > 0 ? `attempt ${job.attempts}/${job.max_attempts}` : `attempt ${job.attempts}`;

  // White-on-tinted-column lifts each card off the column tint without
  // doubling up on colour. The 1px coloured border is the service tag \u2014
  // no per-card service label needed since the column header already names
  // the service.
  return (
    <Link to={jobLink(job)}
      className={`flex items-start gap-2.5 p-2.5 rounded-lg border bg-surface ${waiting ? 'border-amber-200' : palette.border} hover:shadow-sm transition-shadow`}>
      <div className={`flex-shrink-0 h-7 w-7 rounded-md ${waiting ? 'bg-amber-100' : palette.iconBg} flex items-center justify-center mt-0.5`}>
        {waiting
          ? <Clock className="h-3.5 w-3.5 text-amber-600" />
          : <Loader2 className={`h-3.5 w-3.5 ${palette.icon} animate-spin`} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-grey-900 truncate">{jobTitle(job)}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <p className={`text-xs ${waiting ? 'text-amber-600' : palette.text} truncate flex-1 min-w-0`}>
            {subtitleParts.join(' \u00b7 ')}{waiting ? ' \u00b7 waiting' : ''}
          </p>
          {time && <span className={`text-[11px] flex-shrink-0 ${waiting ? 'text-amber-500 font-semibold' : 'text-grey-400'}`}>{time}</span>}
        </div>
        {retryReason && (
          <p className="text-[11px] text-danger-600 truncate mt-0.5" title={`${attemptLabel} · ${retryReason}`}>
            <span className="font-semibold">{attemptLabel}</span> · {retryReason}
          </p>
        )}
      </div>
    </Link>
  );
}

function TimelineRow({ job }: { job: DashboardJob }) {
  const cfg = JOB_CONFIG[job.job_type] || JOB_CONFIG.process;
  const isSuccess = job.status === 'completed';

  return (
    <Link to={jobLink(job)} className="flex items-center gap-3 px-4 py-3 hover:bg-grey-50 transition-colors group">
      <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${isSuccess ? 'bg-success-100' : 'bg-danger-100'}`}>
        {isSuccess
          ? <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />
          : <AlertTriangle className="h-3.5 w-3.5 text-danger-600" />
        }
      </div>
      <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
        {cfg.label}
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-grey-800 truncate block group-hover:text-primary-700">
          {jobTitle(job)}
        </span>
        {jobResultSummary(job) && (
          <span className="text-xs text-grey-400 block truncate">{jobResultSummary(job)}</span>
        )}
      </div>
      {job.company_code && (
        <span className="hidden sm:inline text-xs text-grey-400 flex-shrink-0">{['PRIVATE', 'OTC', 'OTCQX', 'OTCQB', 'PINK'].includes(job.company_code) ? 'Research' : job.company_code}</span>
      )}
      <span className="text-xs text-grey-400 flex-shrink-0 tabular-nums">{timeAgo(job.updated_at)}</span>
    </Link>
  );
}

/**
 * One muted line for the standing backlogs (2026-09-23 operator ruling:
 * "collapse to one line").
 *
 * These queues are worked deliberately on their own pages, not triaged from
 * the dashboard, and as separate rows they pushed the day's real signals
 * below the fold. Counts stay visible, each still deep-links, and the one
 * action that belonged to them (archiving aged failures) rides along.
 */
function BacklogLine({ items, action }: {
  items: Array<{ label: string; count: number; to: string; pill?: string; title?: string }>;
  action?: React.ReactNode;
}) {
  const live = items.filter(i => (i.count ?? 0) > 0);
  if (live.length === 0) return null;
  return (
    <div className="lg:col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 rounded-xl border border-grey-200 bg-grey-50/60">
      <span className="text-xs font-semibold uppercase tracking-wider text-grey-400">Backlogs</span>
      {live.map(i => (
        <Link key={i.label} to={i.to} title={i.title}
              className="text-xs text-grey-500 hover:text-grey-800 transition-colors whitespace-nowrap">
          <span className="font-semibold text-grey-700">{i.count.toLocaleString()}</span> {i.label}
          {i.pill && (
            <span className="ml-1 px-1 py-0.5 rounded bg-warning-100 text-warning-700 text-[10px] font-semibold"
                  title="PRIMARY-coverage articles blocked">{i.pill}</span>
          )}
        </Link>
      ))}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

/**
 * Compact entry in the Needs Attention column for the contact +
 * company merge-candidate queues. Renders only when the combined
 * pending count is non-zero — when both queues are empty we stay
 * out of the way so the existing "All clear" empty-state remains
 * meaningful.
 */
function MergeQueueAttentionStrip() {
  const { data: contactRows } = useQuery({
    queryKey: ['merge-candidates', 'contacts', 'pending-count-dashboard'],
    queryFn: () => listContactMergeCandidates('pending', { limit: 1 }),
    refetchInterval: 60_000,
  });
  const { data: companyRows } = useQuery({
    queryKey: ['merge-candidates', 'companies', 'pending-count-dashboard'],
    queryFn: () => listCompanyMergeCandidates('pending', { limit: 1 }),
    refetchInterval: 60_000,
  });
  const contactCount = contactRows?.total ?? 0;
  const companyCount = companyRows?.total ?? 0;
  const total = contactCount + companyCount;
  if (total === 0) return null;

  // Pick the tab with more pending so the deep-link lands the operator
  // on the queue that needs them most. Equal-or-zero defaults to contacts.
  const primaryTab = companyCount > contactCount ? 'companies' : 'contacts';
  const label = (n: number, kind: string) => `${n.toLocaleString()} ${kind}`;

  return (
    <QueueRow
      to={`/entities/merge-review?tab=${primaryTab}`}
      icon={GitMerge} accent="muted"
      label="Merge candidates awaiting review"
      sublabel={contactCount > 0 && companyCount > 0
        ? `${label(contactCount, 'contact')} · ${label(companyCount, 'company')}`
        : contactCount > 0
          ? label(contactCount, 'contact')
          : label(companyCount, 'company')}
      count={total}
    />
  );
}

function AttentionCard({ item }: { item: DashboardAttentionItem }) {
  const isBuild = item.attention_type === 'build_failed';
  const isStale = item.attention_type === 'promote_stale';
  const isFailed = item.attention_type === 'failed_job' || isBuild;
  const isCoverage = item.attention_type === 'coverage_stalled';
  // T-3: a paid coverage obligation was not met. Deep-links to the
  // announcement, because the fix is per-filing (which masthead is missing and
  // why), not per-group like a stalled monitor.
  const isGuaranteeBreach = item.attention_type === 'coverage_guarantee_breach';
  // A5: a mandated event class missed a masthead past the SLA. Same deep-link
  // as the guarantee breach: the fix is per filing.
  const isMandatedBreach = item.attention_type === 'mandated_coverage_breach';
  // Pipeline health: the per-day table on /analytics names the day and counts.
  const isDuplicate = item.attention_type === 'duplicate_generation';
  // Deep-link priority: coverage stalls → the group editor; otherwise
  // specific article → its announcement → fallback list. awaiting_approval
  // always has article_id; failed jobs only when the failure is downstream of
  // article creation. Pre-article failures fall back to the announcement page.
  // A build failure belongs to a masthead, not an article — route to the site
  // so Rebuild is one click away. Before this it fell through to
  // /announcements, which is a dead end for a build.
  const link = isDuplicate
    ? '/analytics'
    : isStale
    // Routes to the masthead, where the promote queue lives. The articles are
    // knowable but the fix is one action per masthead, not twelve.
    ? '/sites'
    : isBuild
    ? '/sites'
    : isCoverage
    ? '/pipeline?tab=groups'
    : (isGuaranteeBreach || isMandatedBreach) && item.announcement_id
    ? `/announcements/${item.announcement_id}`
    : item.article_id
    ? `/articles/${item.article_id}`
    : item.announcement_id
    ? `/announcements/${item.announcement_id}`
    : isFailed
    ? '/announcements'
    : `/articles/${item.item_id}`;

  const tone = isStale
    // Warning, not danger: production is serving something real and correct,
    // just older than preview. Nothing is broken; something is behind.
    ? { border: 'border-warning-200 bg-warning-50 hover:bg-warning-100', chip: 'bg-warning-200', icon: <Clock className="h-4 w-4 text-warning-700" /> }
    : isFailed || isGuaranteeBreach || isMandatedBreach
    ? { border: 'border-danger-200 bg-danger-50 hover:bg-danger-100', chip: 'bg-danger-200', icon: <AlertTriangle className="h-4 w-4 text-danger-700" /> }
    : isCoverage || isDuplicate
    ? { border: 'border-warning-200 bg-warning-50 hover:bg-warning-100', chip: 'bg-warning-200', icon: <AlertTriangle className="h-4 w-4 text-warning-700" /> }
    : { border: 'border-info-200 bg-info-50 hover:bg-info-100', chip: 'bg-info-200', icon: <Eye className="h-4 w-4 text-info-700" /> };

  const subtitle = isStale
    ? `${item.label}${item.at ? ` · newest edit ${timeAgo(item.at)}` : ''}`
    : isBuild
    ? `${item.label}${item.at ? ` · ${timeAgo(item.at)}` : ''}`
    : isFailed
    ? `Failed ${item.label}${item.at ? ` · ${timeAgo(item.at)}` : ''}`
    : isCoverage
    ? (item.detail || 'Coverage stalled')
    : isGuaranteeBreach || isMandatedBreach
    ? `${item.detail || 'Coverage missing'}${item.at ? ` · ${timeAgo(item.at)}` : ''}`
    : isDuplicate
    ? `${item.detail || 'Duplicate articles'}${item.at ? ` · ${timeAgo(item.at)}` : ''}`
    : 'Ready for review';

  return (
    <Link to={link} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${tone.border}`}>
      <div className={`flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${tone.chip}`}>
        {tone.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-grey-900 truncate">{item.title || 'Unknown'}</p>
        <p className="text-xs text-grey-500 mt-0.5">{subtitle}</p>
        {/* The error itself is the triage signal — without it the operator
            can't tell an Anthropic outage from a GitHub 502 (2026-07-27). */}
        {isStale && item.detail && (
          <p className="text-xs text-warning-800 mt-1">{item.detail}</p>
        )}
        {isFailed && item.detail && (
          // A build error IS the diagnosis — "duplicate permalink /news/x/" or
          // an OOM's "Last few GCs" — and clamping it to two lines throws away
          // the part that identifies the cause. Job errors keep the tighter
          // clamp; they are usually one short line.
          <p
            className={`text-[11px] font-mono text-danger-700 mt-1 break-words whitespace-pre-wrap ${
              isBuild ? 'line-clamp-6' : 'line-clamp-2'
            }`}
            title={item.detail}
          >
            {item.detail}
          </p>
        )}
      </div>
      <ArrowRight className="h-4 w-4 text-grey-400 flex-shrink-0 mt-1" />
    </Link>
  );
}
